import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { OrderItem, PagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save orderItem data.
 */
@Injectable()
export class OrderItemService {
    public static INCLUDE_CREATED_BY_USER: string = 'createdByUserAccount';

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all orderItems for the specified event
     */
    public getIndexListResponse(
        orderId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<PagedResponse> {
        const apiRequestUrl: string = this.getApiUrl(orderId, null, includes, queryParams);
        return this.getIndexListResponseByUrl(orderId, apiRequestUrl);
    }

    /**
     * Get order items by a url. This is used for paging through orderItems since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(orderId: string, url: string): Observable<PagedResponse> {
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;
                const included: any[] = response.included;

                const orderItems: OrderItem[] = [];
                for (const orderItem of json.data) {
                    orderItems.push((new OrderItem()).deserialize(orderItem, included));
                }

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    orderItems,
                );
            }));
    }

    /**
     * Get all orderItems for the specified event
     */
    public getIndex(eventId: string, includes: string[] = null, queryParams: string[] = null): Observable<OrderItem[]> {
        return this.getIndexListResponse(eventId, includes, queryParams)
            .pipe(map((listResponse: PagedResponse) => listResponse.recordList));
    }

    /**
     * Add an order item
     */
    public add(orderId: string, productId: string, usawId: string): Observable<OrderItem> {
        const apiRequestUrl: string = this.getApiUrl(orderId, null);

        const json: any = {};
        json.data = {};
        json.data.type = 'orderItem';
        json.data.attributes = {};
        json.data.attributes.productId = productId;
        json.data.attributes.verificationData = {};
        json.data.attributes.verificationData.usawId = usawId;

        return this.http.post(apiRequestUrl, JSON.stringify(json))
            .pipe(map((response: IJsonApiResponse) => (new OrderItem()).deserialize(response, response.included)));
    }

    /**
     * Delete the specified orderItem.
     */
    public delete(eventId: string, orderItemId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, orderItemId);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(orderId: string, orderItemId: string, includes?: string[], queryParams?: string[]): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/orders/${orderId}/order-items/`;

        if (orderItemId) {
            url += orderItemId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
